/**
 * Back testing and the simulator on the backend's AI functions: streamed generation of
 * scenario types and of fabricated scenarios, the stored types and scenarios, bucketing,
 * bulk runs, and the simulator's judge and investigator.
 */

import api from '@/lib/api';
import { postEventStream, StreamFailure } from '@/lib/sse';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type { ApiResponse } from '@/types';
import type { ScenarioCase, SimulationConfig } from '@/modules/simulator/session/scenario-case';
import type {
	BucketRun,
	BulkRun,
	BulkRunItemReview,
	RunMode,
	ScenarioKind,
	TypeReconcileProgress
} from '@/types/websocket.types';
import type { TypeReadiness } from '@/modules/back-testing/readiness/types';

/** One tool a scenario type's decision paths call, and what it returns there. */
export interface ScenarioTypeTool {
	name: string;
	expect: string;
}

/** A scenario type written from a situation the tester described: stored nowhere, carried with the run. */
export interface DescribedScenarioType {
	description: string;
	outcome: string;
	tools: ScenarioTypeTool[];
}

/** One turn the thread writer produces, in the customer's or the agent's voice. */
interface DescribedTurn {
	role: 'customer' | 'agent';
	text: string;
}

/** One decision a decision path takes in the protocol's compiled graph: the condition it followed. */
export interface BranchDecision {
	nodeId: string;
	label: string;
	condition: string;
}

interface BranchTool {
	nodeId: string;
	tool: string;
	provider: string | null;
	label: string;
	/** The step changes something. */
	write: boolean;
}

/** One decision path into an outcome: the decisions it takes and the steps it passes. */
interface DecisionPath {
	nodeIds: string[];
	decisions: BranchDecision[];
	tools: BranchTool[];
}

/** An outcome of the protocol's graph (where it ends and the writes on the way) with the decision paths that reach it. */
export interface ScenarioTypeBranch {
	/** Every step any of its paths passes, the ending last. */
	nodeIds: string[];
	decisions: BranchDecision[];
	tools: BranchTool[];
	ending: { nodeId: string; kind: 'reply' | 'generated_reply' | 'escalation' | 'other'; text: string };
	/** Reached only through decisions' fallbacks into an escalation: the cases the protocol does not describe. */
	fallback: boolean;
	/** The first few decision paths into the outcome. */
	decisionPaths: DecisionPath[];
	/** How many decision paths reach the outcome in all. */
	decisionPathCount: number;
	compiledAt: string | null;
	/** The protocol was recompiled and no longer has this outcome. */
	stale: boolean;
}

export interface GeneratedScenarioType {
	/** The situation the customer is in, one line. */
	description: string;
	/** What the protocol does about it, one line. */
	outcome: string;
	/** Tools its decision paths call, with the result expected from each. */
	tools: ScenarioTypeTool[];
	/** The outcome of the protocol's compiled graph this type exercises. */
	branch: ScenarioTypeBranch;
}

/** What the judge answered about one simulator reply. */
export interface PlaygroundJudgeResult {
	comparison: Record<string, unknown>;
	bucket: string;
	/** The judge did not rate the reply a perfect substitute. */
	flagged: boolean;
	/** The human reply it was judged against, one entry per agent message. */
	humanResponse: string[];
}

/** Server-persisted variants: ids are minted by the backend at save time. */
interface ScenarioTypeWithId extends GeneratedScenarioType {
	id: string;
}

/** One semantic match from the account-wide scenario-type index. */
export interface ScenarioTypeMatch {
	sopId: string;
	/** Space-joined intent slug; render with the capitalize class like every SOP name. */
	sopName: string;
	typeId: string;
	description: string;
	outcome: string;
	/** Cosine similarity, 0..1; relative, not a probability. */
	score: number;
}

export interface ScenarioTypeSearchResult {
	sameSop: ScenarioTypeMatch[];
	otherSops: ScenarioTypeMatch[];
}

/** One tool call captured off a simulation run's thinking events. */
export interface BulkRunToolCall {
	name: string;
	args: Record<string, unknown>;
	output: unknown;
	simulated: boolean;
	/** The result went through the bend to the scenario type. */
	grounded: boolean;
	/** A write, simulated rather than executed. */
	simulatedWrite: boolean;
	/** In a replay, whether the real ticket's recording answered the call; absent outside a replay. */
	recorded?: boolean | null;
	outcome: 'success' | 'error' | 'awaiting_approval' | null;
	durationMs: number | null;
}

/** What a replay did with the real turn's tool calls, as Brahma reported it. */
interface RecordingReport {
	/** The run was told the real turn's time as "now". */
	recordedTimeUsed: boolean;
	/** Workflows the run was held to. */
	workflowsHeld: string[];
}

/** The real turn a run replayed: its tool calls answered the run's before any mock did. */
export interface RunRecording {
	/** When the real turn ran. */
	recordedAt: string | null;
	/** Null when Brahma reported nothing. */
	report: RecordingReport | null;
}

/** One Ask AI in the simulator; a conversation's runs share its id. */
export interface SimulatorRunOrigin {
	kind: 'simulator';
	conversationId: string;
	/** Who asked; null when unknown. */
	userId: string | null;
	channelId: string | null;
}

/** One persisted simulation run, as the run screen's expanded row shows it. */
export interface BulkRunDetail {
	requestId: string;
	scenarioId: string | null;
	ticketId: string | null;
	/** Null for a simulator run of a thread typed by hand. */
	kind: ScenarioKind | null;
	traceId: string | null;
	/** Brahma's Langfuse trace for this run, when the response carried one. */
	langfuseTraceUrl: string | null;
	query: Array<{ role: 'user' | 'assistant'; text: string }>;
	response: string;
	error: string | null;
	/** The handoff note when the run handed the ticket to a person; absent on runs kept before it was recorded. */
	escalation?: string | null;
	/** The real turn the run replayed; null, or absent on older runs, when it ran on its scenario world. */
	recording?: RunRecording | null;
	toolCalls: BulkRunToolCall[];
	/** The human reply the run was judged against, one entry per agent message; empty when there was none. */
	realResponse: string[];
	comparison: Record<string, unknown> | null;
	/** Set when a real reply existed but the judge itself failed. */
	comparisonError: string | null;
	/** The gap investigator's verdict envelope; null until a flagged run is investigated. */
	investigation: Record<string, unknown> | null;
	/** What started the run. */
	origin: { kind: 'bulk_run' } | { kind: 'dataset_run'; datasetId: string; caseId: string } | SimulatorRunOrigin;
}

/** One run in a scenario's history: the detail plus when and in which mode it ran. */
export interface ScenarioRunHistoryItem extends BulkRunDetail {
	mode: RunMode;
	createdAt: string;
}

interface ScenarioRunHistory {
	runs: ScenarioRunHistoryItem[];
	total: number;
}

/** One page of a type's bulk runs, newest first. */
interface BulkRunPage {
	runs: BulkRun[];
	total: number;
}

/** A past ticket's AI turn, recorded before anyone replied: a Mocked run replays the tool answers it got. */
export interface ScenarioRecording {
	/** When the recorded turn ran. */
	recordedAt: string;
}

/**
 * A stored scenario: a past ticket the bucketing placed under a type or under none, or a
 * customer thread fabricated for a type, which has no ticket.
 */
export interface StoredScenario {
	id: string;
	kind: ScenarioKind;
	scenarioTypeId: string | null;
	ticketId: string | null;
	/** The ticket's customer, server-attached; the scenario world's own fields take precedence. */
	customer?: { name: string | null; email: string | null } | null;
	/** Server-attached: null for a ticket with no recorded turn and for a fabricated scenario, absent when unknown. */
	recording?: ScenarioRecording | null;
	description: string;
	query: ScenarioCase['messages'];
	toolOutput: ScenarioCase['simulationConfig'];
	/** Who fabricated it; null on a past ticket. */
	createdBy: number | null;
	createdAt?: string;
	/** How sure the bucketing was, and the fact that decided it. */
	confidence?: 'high' | 'medium' | 'low';
	why?: string;
}

interface StreamedTypeBatch {
	scenarioTypes: ScenarioTypeWithId[];
	persisted: boolean;
}

/** A fabricated customer thread as it streams in, before it is saved and given an id. */
export interface FabricatedPreview {
	description: string;
	query: ScenarioCase['messages'];
}

interface FabricatedBatch {
	scenarios: StoredScenario[];
}

/** How many scenarios of each kind sit under one type. */
export interface ScenarioCounts {
	ticket: number;
	fabricated: number;
}

/**
 * The types of one SOP with a count each and no scenario bodies. A scenario
 * carries a whole customer thread, so the list that only shows how many there
 * are does not pay for loading them.
 */
/** What the last compile did to the SOP's scenario types, by type id. */
export interface TypeReconcileSummary {
	at: string;
	kept: string[];
	/** Reworded to match the edit; id and scenarios kept. */
	updated: string[];
	added: string[];
	/** No longer fits, kept because the type holds scenarios or a dataset. */
	stale: string[];
	removed: { id: string; description: string }[];
}

interface BackTestingTypes {
	types: ScenarioTypeWithId[];
	typesUpdatedAt: string | null;
	/** Scenario counts by type id, and by `none` for the past tickets under no type; a type with none has no key. */
	counts: Record<string, ScenarioCounts>;
	/** Null until a compile reconciles the set, and again once it is regenerated. */
	lastReconcile: TypeReconcileSummary | null;
	/** The reconcile running now; null when none is. */
	reconciling: TypeReconcileProgress | null;
	/** When the last reconcile wrote nothing. */
	reconcileFailedAt: string | null;
	/** The protocol is compiling, so a reconcile of these types follows. */
	protocolCompiling: boolean;
}

interface StreamHandlers<TElement> {
	/** Fired per element in arrival order, already backend-verified. */
	onElement: (element: TElement, index: number) => void;
	signal?: AbortSignal;
}

/**
 * Runs one streaming generation to completion. Elements flow through
 * onElement as the model produces them; the resolved value is the `done`
 * payload, the complete persisted batch that replaces the streamed preview.
 */
async function runStream<TElement, TBatch>(
	name: string,
	input: unknown,
	{ onElement, signal }: StreamHandlers<TElement>
): Promise<TBatch> {
	let done: TBatch | null = null;
	let failure: StreamFailure | null = null;

	await postEventStream(API_ENDPOINTS.AI_FUNCTIONS.STREAM(name), { input }, {
		signal,
		onEvent: (event, data) => {
			if (event === 'element') {
				const { item, index } = data as { item: TElement; index: number };
				onElement(item, index);
			} else if (event === 'done') {
				done = data as TBatch;
			} else if (event === 'error') {
				const { message, errors } = data as { message?: string; errors?: unknown };
				failure = new StreamFailure(message ?? 'Generation failed', errors);
			}
		}
	});

	if (failure) throw failure;
	if (!done) throw new Error('Generation stream ended without a result');
	return done;
}

export const aiFunctionService = {
	/** Streams a type set's generation: each verified type arrives as the model closes it. */
	streamScenarioTypes(
		input: { sopId: string },
		// Elements arrive with their server id, so a type can be selected mid-stream.
		handlers: StreamHandlers<GeneratedScenarioType & { id: string }>
	): Promise<StreamedTypeBatch> {
		return runStream('generate-scenario-types', input, handlers);
	},

	/**
	 * Streams customer threads fabricated for one type: previews as each lands, then the saved
	 * rows. `count` is 1 to 10, clamped by the server to the room under the type's cap.
	 */
	streamFabricatedScenarios(
		input: { sopId: string; scenarioTypeId: string; count: number; brief?: string },
		handlers: StreamHandlers<FabricatedPreview>
	): Promise<FabricatedBatch> {
		return runStream('fabricate-scenarios', input, handlers);
	},

	/** One SOP's stored types and their counts, as the whole team last generated them. */
	async fetchTypes(sopId: string): Promise<BackTestingTypes> {
		const response = await api.get<ApiResponse<BackTestingTypes>>(
			`${API_ENDPOINTS.AI_FUNCTIONS.BACK_TESTING(sopId)}/types`
		);
		return response.data.data;
	},

	/** Each of the SOP's types with its promotion verdict, derived from its latest finished bulk run. */
	async fetchReadiness(sopId: string): Promise<TypeReadiness[]> {
		const response = await api.get<ApiResponse<{ readiness: TypeReadiness[] }>>(API_ENDPOINTS.AI_FUNCTIONS.READINESS(sopId));
		return response.data.data.readiness;
	},

	/**
	 * One scenario from its id alone, carrying the SOP it sits under. For a surface
	 * handed nothing but the id, which is what a link to a run is.
	 */
	async fetchScenario(scenarioId: string): Promise<StoredScenario & { sopId: string }> {
		const response = await api.get<ApiResponse<{ scenario: StoredScenario & { sopId: string } }>>(
			`${API_ENDPOINTS.AI_FUNCTIONS.SCENARIO(scenarioId)}`
		);
		return response.data.data.scenario;
	},

	/** `none` selects the past tickets that fit no type. */
	async fetchScenariosForType(sopId: string, scenarioTypeId: string): Promise<StoredScenario[]> {
		const response = await api.get<ApiResponse<{ scenarios: StoredScenario[] }>>(
			`${API_ENDPOINTS.AI_FUNCTIONS.BACK_TESTING(sopId)}/scenarios`,
			{ params: { scenarioTypeId } }
		);
		return response.data.data.scenarios;
	},

	async deleteScenarioType(sopId: string, typeId: string): Promise<void> {
		await api.delete(`${API_ENDPOINTS.AI_FUNCTIONS.BACK_TESTING(sopId)}/types/${typeId}`);
	},

	async deleteScenario(sopId: string, scenarioId: string): Promise<void> {
		await api.delete(`${API_ENDPOINTS.AI_FUNCTIONS.BACK_TESTING(sopId)}/scenarios/${scenarioId}`);
	},

	async clearBackTesting(sopId: string): Promise<void> {
		await api.delete(API_ENDPOINTS.AI_FUNCTIONS.BACK_TESTING(sopId));
	},

	/** Starts a pass over the SOP's past tickets not yet placed under a type, or returns the run in progress. */
	async startBucketing(sopId: string): Promise<BucketRun> {
		const response = await api.post<ApiResponse<{ run: BucketRun }>>(API_ENDPOINTS.AI_FUNCTIONS.BUCKETING(sopId));
		return response.data.data.run;
	},

	async fetchBucketing(sopId: string): Promise<BucketRun | null> {
		const response = await api.get<ApiResponse<{ run: BucketRun | null }>>(
			API_ENDPOINTS.AI_FUNCTIONS.BUCKETING(sopId)
		);
		return response.data.data.run;
	},

	/** Semantic matches for the query across the account's scenario types, the open SOP separated from the rest. */
	async searchTypes(sopId: string, q: string): Promise<ScenarioTypeSearchResult> {
		const response = await api.get<ApiResponse<ScenarioTypeSearchResult>>(
			API_ENDPOINTS.AI_FUNCTIONS.TYPE_SEARCH(sopId),
			{ params: { q } }
		);
		return response.data.data;
	},

	/**
	 * Runs the type's scenarios (the picked subset when given) and judges each reply; a run in progress is
	 * reported, not restarted. `buId` is the dashboard's brand; the backend refuses it when the SOP belongs to another.
	 */
	async startBulkRun(
		sopId: string,
		typeId: string,
		mode: RunMode = 'simulated',
		scenarioIds?: string[],
		buId?: string | null
	): Promise<BulkRun> {
		const response = await api.post<ApiResponse<{ run: BulkRun }>>(
			API_ENDPOINTS.AI_FUNCTIONS.BULK_RUN(sopId, typeId),
			{ mode, ...(scenarioIds ? { scenarioIds } : {}) },
			{ params: buId ? { bu_id: buId } : undefined }
		);
		return response.data.data.run;
	},

	/** The bulk run named by `runId`, or with none the type's latest; null before the type first ran. */
	async fetchBulkRun(sopId: string, typeId: string, runId: string | null): Promise<BulkRun | null> {
		const response = await api.get<ApiResponse<{ run: BulkRun | null }>>(
			runId
				? API_ENDPOINTS.AI_FUNCTIONS.BULK_RUN_BY_ID(sopId, typeId, runId)
				: API_ENDPOINTS.AI_FUNCTIONS.BULK_RUN(sopId, typeId)
		);
		return response.data.data.run;
	},

	/** The type's bulk runs, newest first. */
	async fetchBulkRuns(sopId: string, typeId: string, offset: number, limit: number): Promise<BulkRunPage> {
		const response = await api.get<ApiResponse<BulkRunPage>>(API_ENDPOINTS.AI_FUNCTIONS.BULK_RUNS(sopId, typeId), {
			params: { offset, limit }
		});
		return response.data.data;
	},

	/**
	 * Marks one settled reply of a finished bulk run Correct or Incorrect, overriding the judge (and
	 * judging a reply that had no human reply to compare); `null` clears the mark. Returns the run.
	 */
	async reviewBulkRunItem(
		sopId: string,
		typeId: string,
		runId: string,
		scenarioId: string,
		verdict: BulkRunItemReview['verdict'] | null,
		note?: string | null
	): Promise<BulkRun> {
		const response = await api.put<ApiResponse<{ run: BulkRun }>>(
			API_ENDPOINTS.AI_FUNCTIONS.BULK_RUN_ITEM_REVIEW(sopId, typeId, runId, scenarioId),
			{ verdict, note: note ?? null }
		);
		return response.data.data.run;
	},

	/** The persisted runs behind one bulk run, or only the one `requestId` names; keyed by requestId on the caller's side. */
	async fetchBulkRunDetails(sopId: string, typeId: string, runId: string, requestId?: string): Promise<BulkRunDetail[]> {
		const response = await api.get<ApiResponse<{ runs: BulkRunDetail[] }>>(
			`${API_ENDPOINTS.AI_FUNCTIONS.BULK_RUN_BY_ID(sopId, typeId, runId)}/details`,
			{ params: requestId ? { requestId } : undefined }
		);
		return response.data.data.runs;
	},

	/**
	 * Judges one simulator reply against the human agent reply on its ticket. Synchronous:
	 * one model call, a few seconds. A 409 means the ticket has no human reply to compare.
	 */
	async judgePlaygroundRun(input: {
		/** Omitted for a run with no scenario behind it; `humanResponse` supplies the reference instead. */
		ticketId?: string | null;
		aiResponse: string;
		customerQuestion?: string;
		/** A reply to judge against, used instead of the one the ticket carries. */
		humanResponse?: string;
	}): Promise<PlaygroundJudgeResult> {
		const response = await api.post<ApiResponse<PlaygroundJudgeResult>>(
			API_ENDPOINTS.AI_FUNCTIONS.PLAYGROUND_JUDGE,
			input
		);
		return response.data.data;
	},

	/** The scenario type a described situation is a test of, naming the brand's tools; null when the AI could not write one. */
	async describeScenarioType(
		input: { situation: string },
		buId?: string | null
	): Promise<DescribedScenarioType | null> {
		const response = await api.post<ApiResponse<{ output: { scenarioType: DescribedScenarioType | null } }>>(
			API_ENDPOINTS.AI_FUNCTIONS.RUN('describe-scenario-type'),
			{ input },
			{ params: buId ? { bu_id: buId } : undefined }
		);
		return response.data.data.output.scenarioType;
	},

	/** The sidebar's world for a described situation: the records its scenario type starts from, built around what the tester already set. */
	async describeScenarioWorld(input: {
		situation: string;
		scenarioType: DescribedScenarioType;
		known?: SimulationConfig;
	}): Promise<SimulationConfig> {
		const response = await api.post<ApiResponse<{ output: { toolOutput: SimulationConfig } }>>(
			API_ENDPOINTS.AI_FUNCTIONS.RUN('describe-scenario-world'),
			{ input }
		);
		return response.data.data.output.toolOutput;
	},

	/** The customer's opening turns for a described situation, written against its world. */
	async describeScenarioThread(input: {
		situation: string;
		scenarioType: DescribedScenarioType;
		toolOutput: Record<string, unknown>;
	}): Promise<DescribedTurn[]> {
		const response = await api.post<ApiResponse<{ output: { messages: DescribedTurn[] } }>>(
			API_ENDPOINTS.AI_FUNCTIONS.RUN('describe-scenario-thread'),
			{ input }
		);
		return response.data.data.output.messages;
	},

	/** Starts the root-cause investigation of one simulator run; the milestones and the verdict arrive on the socket. */
	async investigatePlaygroundRun(input: {
		requestId: string;
		langfuseTraceUrl: string;
		bucket?: string | null;
		ticketId?: string | null;
	}): Promise<void> {
		await api.post<ApiResponse<{ requestId: string; status: string }>>(
			API_ENDPOINTS.AI_FUNCTIONS.PLAYGROUND_INVESTIGATE,
			input
		);
	},

	/** One scenario's persisted runs, newest first. */
	async fetchScenarioRuns(
		sopId: string,
		scenarioId: string,
		offset: number,
		limit: number
	): Promise<ScenarioRunHistory> {
		const response = await api.get<ApiResponse<ScenarioRunHistory>>(
			API_ENDPOINTS.AI_FUNCTIONS.SCENARIO_RUNS(sopId, scenarioId),
			{ params: { offset, limit } }
		);
		return response.data.data;
	}
};
